import { useState, useEffect, useMemo, useRef } from 'react'
import { useUIStore } from '@/stores/ui.store'
import { supabase } from '@/config/supabase'
import { formatDate, translateStatus, getStatusBadgeClass } from '@/lib/formatters'
import { FaturaDetailDrawer } from '@/components/admin/FaturaDetailDrawer'
import { useMarcarFaturaPaga, useGerarProRata } from '@/hooks/useFaturas'
import {
    Search, Download, RefreshCw, Car, Loader2, Filter,
    Trash2, DollarSign, CreditCard, Edit3, Eye, EyeOff, Plus,
    X, Check, AlertTriangle, ChevronDown, ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react'

// ── Constantes ────────────────────────────────────────────────────────────────

const PLACA_REGEX = /\b([A-Z]{3}[0-9][A-Z0-9][0-9]{2})\b/i

const BILLING_LABELS: Record<string, string> = {
    BOLETO: 'Boleto',
    CREDIT_CARD: 'Cartão',
    PIX: 'Pix',
    TRANSFER: 'Transferência',
    DEPOSIT: 'Depósito',
    RECEIVED_IN_CASH: 'Dinheiro',
    UNDEFINED: 'N/D',
}
function traduzBilling(b?: string) {
    if (!b) return 'N/D'
    const label = BILLING_LABELS[b] || BILLING_LABELS[b.toUpperCase()]
    return label ?? b
}

const SERVICOS = [
    { key: 'rastreador', label: 'Rastreador', pattern: /rastreador/i },
    { key: 'tac', label: 'TAC', pattern: /\bTAC\b/i },
    { key: 'tav', label: 'TAV', pattern: /\bTAV\b/i },
    { key: 'caucao', label: 'Caução', pattern: /cau[çc][aã]o/i },
]

import { asaasService } from '@/services/asaas.service'

function detectarServico(desc: string): string | null {
    for (const s of SERVICOS) { if (s.pattern.test(desc)) return s.key }
    return null
}

function extrairPlaca(desc?: string, extRef?: string): string | null {
    if (desc) {
        const m = desc.match(/[Pp]laca[:\s]+([A-Z]{3}[0-9][A-Z0-9][0-9]{2})/i)
        if (m) return m[1]!.toUpperCase()
        const m2 = desc.match(PLACA_REGEX)
        if (m2) return m2[1]!.toUpperCase()
    }
    if (extRef) {
        const m = extRef.match(PLACA_REGEX)
        if (m) return m[1]!.toUpperCase()
    }
    return null
}

// ── Types ─────────────────────────────────────────────────────────────────────

type CobrancaRow = {
    id: string
    valor: number
    data_vencimento: string
    data_pagamento: string | null
    status: string
    placa: string
    descricao: string
    associado_nome: string
    billingType: string
    servico: string | null
    deletedAt: string | null
    metadata: any
    tipo: string
    contrato_id: string | null
    associado_id: string | null
    url_boleto: string | null
    url_pix: string | null
    asaas_payment_id: string | null
}

// ── Modal Alterar Tipo ────────────────────────────────────────────────────────

function ModalAlterarTipo({ row, taxasDb, onClose, onSave }: { row: CobrancaRow; taxasDb: any[]; onClose: () => void; onSave: (tipo: string) => Promise<void> }) {
    const [tipo, setTipo] = useState(row.billingType)
    const [saving, setSaving] = useState(false)

    const taxaRef = taxasDb.find(t => t.tipo === tipo)
    const pct = taxaRef ? Number(taxaRef.percentual_taxa) : 0
    const fixo = taxaRef ? Number(taxaRef.valor_fixo) : 0
    const taxa = (row.valor * (pct / 100)) + fixo
    const liquido = row.valor - taxa

    async function handle() {
        setSaving(true)
        await onSave(tipo)
        setSaving(false)
        onClose()
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-fade-in" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-bold text-surface-900">Alterar Tipo de Cobrança</h3>
                    <button onClick={onClose} className="p-1 rounded-lg hover:bg-surface-100 text-surface-700"><X size={16} /></button>
                </div>
                <p className="text-sm text-surface-700/60 mb-3">Placa <strong className="font-mono">{row.placa}</strong> · R$ {row.valor.toFixed(2)}</p>
                <select value={tipo} onChange={e => setTipo(e.target.value)} className="input mb-3">
                    <option value="BOLETO">Boleto</option>
                    <option value="PIX">Pix</option>
                    <option value="CREDIT_CARD">Cartão de Crédito</option>
                    <option value="TRANSFER">Transferência</option>
                </select>
                {taxa > 0 && (
                    <div className="bg-warning-50 border border-warning-200 rounded-xl p-3 mb-4 text-sm">
                        <p className="font-semibold text-warning-700">Comissão / Taxa: {pct > 0 ? `${pct}%` : ''} {fixo > 0 ? `+ R$ ${fixo}` : ''}</p>
                        <p className="text-warning-600">Taxa: R$ {taxa.toFixed(2)} → Líquido: <strong>R$ {liquido.toFixed(2)}</strong></p>
                    </div>
                )}
                <div className="flex gap-2">
                    <button onClick={onClose} className="btn btn-secondary flex-1">Cancelar</button>
                    <button onClick={handle} disabled={saving} className="btn btn-primary flex-1 gap-2">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Salvar
                    </button>
                </div>
            </div>
        </div>
    )
}

// ── Modal Alterar Valor ───────────────────────────────────────────────────────

function ModalAlterarValor({ row, onClose, onSave }: { row: CobrancaRow; onClose: () => void; onSave: (valor: number) => Promise<void> }) {
    const [valor, setValor] = useState(String(row.valor.toFixed(2)))
    const [saving, setSaving] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    useEffect(() => { inputRef.current?.select() }, [])

    async function handle() {
        const v = parseFloat(valor.replace(',', '.'))
        if (isNaN(v) || v <= 0) return
        setSaving(true)
        await onSave(v)
        setSaving(false)
        onClose()
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-fade-in" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-bold text-surface-900">Alterar Valor</h3>
                    <button onClick={onClose} className="p-1 rounded-lg hover:bg-surface-100 text-surface-700"><X size={16} /></button>
                </div>
                <p className="text-sm text-surface-700/60 mb-3">Placa <strong className="font-mono">{row.placa}</strong></p>
                <div className="flex items-center gap-2 mb-4">
                    <span className="text-sm font-semibold text-surface-700">R$</span>
                    <input ref={inputRef} type="number" step="0.01" min="0" value={valor}
                        onChange={e => setValor(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handle() }}
                        className="input flex-1 text-lg font-bold" />
                </div>
                <div className="flex gap-2">
                    <button onClick={onClose} className="btn btn-secondary flex-1">Cancelar</button>
                    <button onClick={handle} disabled={saving} className="btn btn-primary flex-1 gap-2">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Salvar
                    </button>
                </div>
            </div>
        </div>
    )
}

// ── Modal Nova Cobrança ───────────────────────────────────────────────────────

function ModalNovaCobranca({
    templatesDb, onClose, onSave
}: {
    templatesDb: any[];
    onClose: () => void; onSave: (dados: any) => Promise<void>;
}) {
    const [saving, setSaving] = useState(false)
    const [buscando, setBuscando] = useState(false)
    const [associado, setAssociado] = useState<any>(null)
    const [veiculo, setVeiculo] = useState<any>(null)

    // Form
    const [placaBusca, setPlacaBusca] = useState('')
    const [tipoCobranca, setTipoCobranca] = useState('mensalidade') // do template
    const [formaPgto, setFormaPgto] = useState('PIX') // credito/boleto/pix
    const [valor, setValor] = useState('')
    const [vencimento, setVencimento] = useState(() => {
        const d = new Date(); d.setDate(d.getDate() + 3);
        return d.toISOString().split('T')[0]
    })
    const [descricao, setDescricao] = useState('')

    // Efeito para preencher a descrição baseada no template quando associado/veiculo ou tipo_cobranca mudam
    useEffect(() => {
        const tmpl = templatesDb.find(t => t.tipo_cobranca === tipoCobranca)?.template || ''
        if (!tmpl) return
        let finalStr = tmpl
            .replace(/\{\{placa\}\}/g, veiculo?.placa || '')
            .replace(/\{\{nome\}\}/g, associado?.nome || '')
            .replace(/\{\{cpf\}\}/g, associado?.cpf_cnpj || '')
            .replace(/\{\{entidade\}\}/g, associado?.asaas_empresa || '')
            .replace(/\{\{mes\}\}/g, String(new Date(vencimento || Date.now()).getMonth() + 1).padStart(2, '0'))
            .replace(/\{\{ano\}\}/g, String(new Date(vencimento || Date.now()).getFullYear()))
        setDescricao(finalStr)
    }, [tipoCobranca, associado, veiculo, vencimento, templatesDb])

    async function handleBuscarPlaca() {
        if (!placaBusca || placaBusca.length < 7) return
        setBuscando(true)
        try {
            const placaFormatada = placaBusca.toUpperCase().replace(/[^A-Z0-9]/g, '')
            const { data } = await supabase.schema('veicular_system').from('veic_veiculos')
                .select('*, associado:veic_associados(*)').eq('placa', placaFormatada).maybeSingle()
            if (data) {
                setVeiculo(data)
                setAssociado(data.associado)
            } else {
                alert('Placa não encontrada.')
            }
        } finally {
            setBuscando(false)
        }
    }

    async function handleSave() {
        if (!associado || !valor || !vencimento) return alert('Preencha os dados e busque uma placa válida.')
        setSaving(true)
        await onSave({
            associado_id: associado.id,
            veiculo_id: veiculo?.id || null,
            valor: parseFloat(valor.replace(',', '.')),
            vencimento,
            descricao,
            formaPgto,
            tipoCobranca,
            asaas_empresa: associado.asaas_empresa
        })
        setSaving(false)
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-lg animate-fade-in" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-surface-900">Nova Cobrança</h3>
                    <button onClick={onClose} className="p-1 rounded-lg hover:bg-surface-100 text-surface-700"><X size={16} /></button>
                </div>

                <div className="space-y-4">
                    {/* Busca */}
                    <div className="flex gap-2 items-end">
                        <div className="flex-1">
                            <label className="text-xs font-semibold text-surface-700">Placa do Veículo</label>
                            <input type="text" value={placaBusca} onChange={e => setPlacaBusca(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleBuscarPlaca() }}
                                placeholder="ABC1234" maxLength={8} className="input uppercase" />
                        </div>
                        <button onClick={handleBuscarPlaca} disabled={buscando} className="btn btn-secondary mb-[1px]">
                            {buscando ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                        </button>
                    </div>

                    {/* Dados Associado */}
                    {associado && (
                        <div className="p-3 bg-primary-50 border border-primary-100 rounded-xl text-sm">
                            <p className="font-bold text-primary-900">{associado.nome}</p>
                            <p className="text-primary-700">CPF: {associado.cpf_cnpj} · {associado.asaas_empresa}</p>
                            {veiculo && <p className="text-primary-700 mt-1">Veículo: <strong className="font-mono bg-white px-1 rounded">{veiculo.placa}</strong></p>}
                        </div>
                    )}

                    {/* Tipo e Templating */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-semibold text-surface-700">Motivo (Template)</label>
                            <select value={tipoCobranca} onChange={e => setTipoCobranca(e.target.value)} className="input">
                                {templatesDb.filter(t => t.ativo).map(t => (
                                    <option key={t.id} value={t.tipo_cobranca}>{t.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-surface-700">Forma de Pgto</label>
                            <select value={formaPgto} onChange={e => setFormaPgto(e.target.value)} className="input">
                                <option value="BOLETO">Boleto</option>
                                <option value="PIX">Pix</option>
                                <option value="CREDIT_CARD">Cartão</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-semibold text-surface-700">Valor (R$)</label>
                            <input type="number" step="0.01" value={valor} onChange={e => setValor(e.target.value)} className="input" placeholder="0.00" />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-surface-700">Vencimento</label>
                            <input type="date" value={vencimento} onChange={e => setVencimento(e.target.value)} className="input" />
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-semibold text-surface-700">Descrição (aparece no boleto)</label>
                        <textarea value={descricao} onChange={e => setDescricao(e.target.value)} rows={3} className="input text-sm resize-none"></textarea>
                    </div>

                </div>

                <div className="flex gap-2 mt-6">
                    <button onClick={onClose} className="btn btn-secondary flex-1">Cancelar</button>
                    <button onClick={handleSave} disabled={saving || !associado} className="btn btn-primary flex-1 gap-2">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Confirmar Nova
                    </button>
                </div>
            </div>
        </div>
    )
}

// ── Modal Confirma Exclusão ───────────────────────────────────────────────────

function ModalConfirmarExclusao({ row, onClose, onConfirm }: { row: CobrancaRow; onClose: () => void; onConfirm: () => Promise<void> }) {
    const [saving, setSaving] = useState(false)

    async function handle() {
        setSaving(true)
        await onConfirm()
        setSaving(false)
        onClose()
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-fade-in text-center" onClick={e => e.stopPropagation()}>
                <div className="w-16 h-16 bg-danger-50 text-danger-600 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-danger-100">
                    <AlertTriangle size={28} />
                </div>
                <h3 className="text-xl font-bold text-surface-900 mb-2">Excluir Cobrança?</h3>
                <p className="text-sm text-surface-600 mb-2">
                    Você está prestes a cancelar a fatura da placa <strong className="font-mono text-surface-900 bg-surface-100 px-1 rounded">{row.placa}</strong> no valor de <strong>R$ {row.valor.toFixed(2)}</strong>.
                </p>
                <div className="text-xs text-danger-600 bg-danger-50 p-2 rounded-lg mb-6 text-left border border-danger-100 font-medium">
                    Asaas: A cobrança será cancelada definitivamente caso exista no banco Asaas. Essa ação não poderá ser desfeita.
                </div>
                <div className="flex gap-2">
                    <button onClick={onClose} disabled={saving} className="btn btn-secondary flex-1">Cancelar</button>
                    <button onClick={handle} disabled={saving} className="btn bg-danger-600 hover:bg-danger-700 text-white flex-1 gap-2">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Excluir
                    </button>
                </div>
            </div>
        </div>
    )
}

// ── Modal Receber em Dinheiro ──────────────────────────────────────────────────

function ModalReceberEmDinheiro({ row, onClose, onConfirm }: { row: CobrancaRow; onClose: () => void; onConfirm: (dados: { valor: number, data: string }) => Promise<void> }) {
    const [saving, setSaving] = useState(false)
    const [valor, setValor] = useState(String(row.valor.toFixed(2)))
    const [dataPgto, setDataPgto] = useState(() => new Date().toISOString().split('T')[0])

    async function handle() {
        const v = parseFloat(valor.replace(',', '.'))
        if (isNaN(v) || v <= 0 || !dataPgto) return alert('Preencha os campos corretamente.')

        setSaving(true)
        await onConfirm({ valor: v, data: dataPgto })
        setSaving(false)
        onClose()
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-fade-in" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-surface-900">Receber em Dinheiro</h3>
                    <button onClick={onClose} className="p-1 rounded-lg hover:bg-surface-100 text-surface-700"><X size={16} /></button>
                </div>

                <p className="text-sm text-surface-600 mb-4">
                    Baixar a fatura <strong className="font-mono text-surface-900">{row.placa}</strong> no Asaas como recebida em dinheiro.
                </p>

                <div className="space-y-3 mb-6">
                    <div>
                        <label className="text-xs font-semibold text-surface-700">Valor Recebido (R$)</label>
                        <input type="number" step="0.01" min="0" value={valor} onChange={e => setValor(e.target.value)} className="input" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-surface-700">Data do Pagamento</label>
                        <input type="date" value={dataPgto} onChange={e => setDataPgto(e.target.value)} className="input" />
                    </div>
                </div>

                <div className="flex gap-2">
                    <button onClick={onClose} disabled={saving} className="btn btn-secondary flex-1">Cancelar</button>
                    <button onClick={handle} disabled={saving} className="btn bg-success-600 hover:bg-success-700 text-white flex-1 gap-2">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <DollarSign size={14} />} Confirmar
                    </button>
                </div>
            </div>
        </div>
    )
}

// ── Página Principal ──────────────────────────────────────────────────────────

export function CobrancasPlacaPage() {
    const setPageTitle = useUIStore(s => s.setPageTitle)
    const [rows, setRows] = useState<CobrancaRow[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [statusFiltro, setStatusFiltro] = useState('todos')
    const [tipoFiltro, setTipoFiltro] = useState('todos')
    const [servicoFiltro, setServicoFiltro] = useState<Record<string, 'include' | 'exclude'>>({})
    const [isServicoMenuOpen, setIsServicoMenuOpen] = useState(false)
    const servicoMenuRef = useRef<HTMLDivElement>(null)

    const [exibirExcluidas, setExibirExcluidas] = useState(false)
    const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' } | null>({ key: 'data_vencimento', direction: 'desc' })
    const [selectedFatura, setSelectedFatura] = useState<any | null>(null)
    const [modalTipo, setModalTipo] = useState<CobrancaRow | null>(null)
    const [modalValor, setModalValor] = useState<CobrancaRow | null>(null)
    const [modalExclusao, setModalExclusao] = useState<CobrancaRow | null>(null)
    const [modalDinheiro, setModalDinheiro] = useState<CobrancaRow | null>(null)
    const [modalNova, setModalNova] = useState(false)

    // Dados DB para fase 2 e 4
    const [taxasDb, setTaxasDb] = useState<any[]>([])
    const [templatesDb, setTemplatesDb] = useState<any[]>([])

    const hoje = new Date()
    const inicioMes = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-01`
    const fimMes = (() => { const d = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` })()
    const [dataInicio, setDataInicio] = useState(inicioMes)
    const [dataFim, setDataFim] = useState(fimMes)

    const marcarPagaMutation = useMarcarFaturaPaga()
    const proRataMutation = useGerarProRata()

    useEffect(() => { setPageTitle('Cobranças') }, [setPageTitle])

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (servicoMenuRef.current && !servicoMenuRef.current.contains(event.target as Node)) {
                setIsServicoMenuOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    function toggleServicoFilter(key: string) {
        setServicoFiltro(prev => {
            const next = { ...prev }
            const current = next[key]
            if (!current) next[key] = 'include'
            else if (current === 'include') next[key] = 'exclude'
            else delete next[key]
            return next
        })
    }

    const [syncState, setSyncState] = useState({ running: false, done: 0, total: 0, errors: 0 })

    useEffect(() => {
        supabase.schema('veicular_system').from('veic_taxas_cobranca').select('*').then(({ data }) => setTaxasDb(data || []))
        supabase.schema('veicular_system').from('veic_descricao_templates').select('*').then(({ data }) => setTemplatesDb(data || []))
    }, [])

    async function handleSyncAsaas() {
        if (!confirm('Deseja iniciar a sincronização com o Asaas para buscar novas faturas e atualizar status? Isso varrerá as contas Asaas configuradas salvando as faturas cujo ID do Asaas seja identificado ou a Placa (via Regex) esteja na descrição do boleto.')) return

        // Pega todos associados com asaas_customer_id
        const allData: { id: string }[] = []
        let from = 0
        while (true) {
            const { data, error } = await supabase
                .schema('veicular_system')
                .from('veic_associados')
                .select('id')
                .not('asaas_customer_id', 'is', null)
                .is('deleted_at', null)
                .range(from, from + 999)
            if (error) { alert(`Erro ao listar associados: ${error.message}`); return }
            if (!data?.length) break
            allData.push(...data)
            if (data.length < 1000) break
            from += 1000
        }

        if (!allData.length) { alert('Nenhum associado com Asaas ID encontrado no banco.'); return }

        setSyncState({ running: true, done: 0, total: allData.length, errors: 0 })
        let erros = 0
        try {
            for (let i = 0; i < allData.length; i++) {
                try {
                    await asaasService.syncCharges(allData[i]!.id)
                } catch (e: any) {
                    console.error('Erro na sincronizacão do associado', allData[i]!.id, e)
                    erros++
                }
                setSyncState(s => ({ ...s, done: i + 1, errors: erros }))
                if (i < allData.length - 1) await new Promise(r => setTimeout(r, 200))
            }
        } finally {
            setSyncState(s => ({ ...s, running: false }))
            alert(`Sincronização concluída. ${allData.length} clientes verificados. ${erros} com erro.`)
            carregar() // Recarrega a tabela com as novas faturas do DB local
        }
    }

    async function carregar() {
        setLoading(true)
        try {
            const allData: any[] = []
            let offset = 0
            const PAGE = 1000
            while (true) {
                let q = supabase
                    .schema('veicular_system')
                    .from('veic_faturas')
                    .select('*, associado:veic_associados(nome, asaas_empresa), contrato:veic_contratos(veiculo:veic_veiculos(placa))')
                    .in('status', ['paga', 'pendente', 'atrasada'])
                    .gte('data_vencimento', dataInicio)
                    .lte('data_vencimento', dataFim)
                    .order('data_vencimento', { ascending: false })
                    .range(offset, offset + PAGE - 1)

                if (!exibirExcluidas) q = q.is('deleted_at', null)

                const { data, error } = await q
                if (error) throw error
                allData.push(...(data || []))
                if (!data || data.length < PAGE) break
                offset += PAGE
            }

            const comPlaca: CobrancaRow[] = []
            for (const f of allData) {
                const meta = f.metadata || {}
                const desc = meta.description || meta.externalReference || ''
                const extRef = meta.externalReference || ''
                const placa = extrairPlaca(desc, extRef) || meta._placa_extraida || (f as any).contrato?.veiculo?.placa || null
                if (!placa) continue
                comPlaca.push({
                    id: f.id,
                    valor: f.valor,
                    data_vencimento: f.data_vencimento,
                    data_pagamento: f.data_pagamento,
                    status: f.status,
                    placa,
                    descricao: desc,
                    associado_nome: (f.associado as any)?.nome || '—',
                    billingType: meta.billingType || 'UNDEFINED',
                    servico: detectarServico(desc),
                    deletedAt: f.deleted_at || null,
                    metadata: { ...meta, _empresa: (f.associado as any)?.asaas_empresa || null },
                    tipo: f.tipo || 'mensalidade',
                    contrato_id: f.contrato_id || null,
                    associado_id: f.associado_id || null,
                    url_boleto: f.url_boleto || null,
                    url_pix: f.url_pix || null,
                    asaas_payment_id: f.asaas_payment_id || null,
                })
            }
            setRows(comPlaca)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { carregar() }, [dataInicio, dataFim, exibirExcluidas])

    // ── Ações ────────────────────────────────────────────────────────────────

    async function excluirConfirmado(row: CobrancaRow) {
        setLoading(true)
        try {
            const { data, error } = await supabase.functions.invoke('asaas-cancel-charge', {
                body: {
                    fatura_id: row.id,
                    asaas_payment_id: row.asaas_payment_id,
                    asaas_empresa: row.metadata?._empresa
                }
            })

            if (error) {
                console.error(error)
                alert('Erro ao excluir (possivelmente a Edge Function não está rodando): ' + error.message)
            } else if (!data?.success) {
                alert('Erro na resposta do Supabase: ' + JSON.stringify(data))
            }
        } catch (err: any) {
            alert('Erro de conexão ou CORS ao tentar excluir: ' + err.message)
        }
        carregar()
    }

    async function pagoEmDinheiro(row: CobrancaRow, dados: { valor: number, data: string }) {
        setLoading(true)
        try {
            const { data: resData, error } = await supabase.functions.invoke('asaas-receive-cash', {
                body: {
                    fatura_id: row.id,
                    asaas_payment_id: row.asaas_payment_id,
                    asaas_empresa: row.metadata?._empresa,
                    valor: dados.valor,
                    data_pagamento: dados.data,
                    metadata: row.metadata
                }
            })

            if (error) {
                console.error(error)
                alert('Erro na Edge Function: ' + error.message)
            } else if (!resData?.success) {
                alert('Erro ao processar: ' + resData?.error)
            } else {
                if (resData.success && !resData.asaasSuccess) {
                    alert('Fatura baixada no banco local, porém o Asaas retornou: ' + resData.asaasError)
                }

                // Otimista: atualiza a tab local e depois recarrega do banco pra evitar race conditions com o delay da Edge Function
                setRows(rs => rs.map(r => r.id === row.id ? { ...r, status: 'paga', data_pagamento: dados.data } : r))
            }
        } catch (err: any) {
            alert('Erro de conexão ou CORS ao tentar baixar: ' + err.message)
        }

        await new Promise(r => setTimeout(r, 800))
        carregar()
    }

    async function alterarTipo(row: CobrancaRow, novoTipo: string) {
        const { error } = await supabase
            .schema('veicular_system')
            .from('veic_faturas')
            .update({ metadata: { ...row.metadata, billingType: novoTipo } })
            .eq('id', row.id)
        if (error) throw error
        carregar()
    }

    async function alterarValor(row: CobrancaRow, novoValor: number) {
        const { error } = await supabase
            .schema('veicular_system')
            .from('veic_faturas')
            .update({ valor: novoValor })
            .eq('id', row.id)
        if (error) throw error
        carregar()
    }

    async function criarNovaCobranca(dados: any) {
        // Por ora cria sem Asaas ID e com status pendente no DB
        const f = {
            associado_id: dados.associado_id,
            contrato_id: dados.veiculo_id, // Usando contrato_id = veiculo_id provisoriamente
            valor: dados.valor,
            data_vencimento: dados.vencimento,
            status: 'pendente',
            tipo: dados.tipoCobranca,
            metadata: {
                billingType: dados.formaPgto,
                description: dados.descricao,
                _empresa: dados.asaas_empresa,
                fonte: 'assistente_manual'
            }
        }
        const { error } = await supabase.schema('veicular_system').from('veic_faturas').insert(f)
        if (error) { alert('Erro: ' + error.message); return }
        setModalNova(false)
        carregar()
    }

    // ── Filtros ──────────────────────────────────────────────────────────────

    const filtradas = useMemo(() => {
        return rows.filter(r => {
            if (statusFiltro !== 'todos' && r.status !== statusFiltro) return false
            if (tipoFiltro !== 'todos' && r.billingType !== tipoFiltro) return false

            // Lógica do filtro avaçado de serviços
            const includes = Object.entries(servicoFiltro).filter(([_, v]) => v === 'include').map(([k]) => k)
            const excludes = Object.entries(servicoFiltro).filter(([_, v]) => v === 'exclude').map(([k]) => k)
            const rowServico = r.servico || ''

            if (includes.length > 0 && !includes.includes(rowServico)) return false
            if (excludes.length > 0 && excludes.includes(rowServico)) return false

            if (search) {
                const s = search.toUpperCase()
                if (!r.placa.includes(s) && !r.associado_nome.toUpperCase().includes(s)) return false
            }
            return true
        })
    }, [rows, search, statusFiltro, tipoFiltro, servicoFiltro])

    const sortedFiltradas = useMemo(() => {
        let sortableItems = [...filtradas]
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                let aVal: any = a[sortConfig.key as keyof CobrancaRow]
                let bVal: any = b[sortConfig.key as keyof CobrancaRow]

                if (sortConfig.key === 'tempo') {
                    // Ordenação pelo tempo de pagamento ("Em dia", atrasos, etc)
                    const getTempo = (r: CobrancaRow) => {
                        if (!r.data_pagamento) return 0
                        return calcDiasDiff(r.data_pagamento, r.data_vencimento)
                    }
                    aVal = getTempo(a)
                    bVal = getTempo(b)
                }

                if (aVal === null || aVal === undefined) aVal = ''
                if (bVal === null || bVal === undefined) bVal = ''

                if (typeof aVal === 'string') aVal = aVal.toLowerCase()
                if (typeof bVal === 'string') bVal = bVal.toLowerCase()

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
                return 0
            })
        }
        return sortableItems
    }, [filtradas, sortConfig])

    function requestSort(key: string) {
        let direction: 'asc' | 'desc' = 'asc'
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc'
        setSortConfig({ key, direction })
    }

    function getSortIcon(key: string) {
        if (!sortConfig || sortConfig.key !== key) return <ArrowUpDown size={14} className="opacity-30 inline ml-1" />
        return sortConfig.direction === 'asc' ? <ArrowUp size={14} className="text-primary-600 inline ml-1" /> : <ArrowDown size={14} className="text-primary-600 inline ml-1" />
    }

    // ── Totais ───────────────────────────────────────────────────────────────

    function exportCSV() {
        const header = ['Placa', 'Associado', 'Tipo', 'Valor', 'Vencimento', 'Pagamento', 'Status', 'Serviço', 'Descrição']
        const csvRows = [
            header.join(';'),
            ...filtradas.map(r => [
                r.placa, r.associado_nome, traduzBilling(r.billingType),
                r.valor.toFixed(2).replace('.', ','), r.data_vencimento,
                r.data_pagamento || '', r.status,
                SERVICOS.find(s => s.key === r.servico)?.label || '',
                `"${r.descricao.replace(/"/g, '""')}"`,
            ].join(';')),
        ]
        const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `cobrancas_placa_${new Date().toISOString().split('T')[0]}.csv`
        link.click()
    }

    const totais = useMemo(() => {
        const totalRows = rows.length || 1
        const pct = (n: number) => ((n / totalRows) * 100).toFixed(1) + '%'
        const pagas = filtradas.filter(r => r.status === 'paga')
        const pendentes = filtradas.filter(r => r.status === 'pendente')
        const atrasadas = filtradas.filter(r => r.status === 'atrasada')

        // Cálculo da média de dias para pagamento (adiantado ou atrasado)
        let mediaDias = 0
        let maxAdiantado = 0
        let maxAtrasado = 0
        if (pagas.length > 0) {
            let sumDias = 0
            for (const p of pagas) {
                if (p.data_pagamento && p.data_vencimento) {
                    // Ignora hora, compara datas absolutas
                    const dPgto = new Date(p.data_pagamento + 'T12:00:00')
                    const dVenc = new Date(p.data_vencimento + 'T12:00:00')
                    // diferença em milissegundos
                    const diffTime = dPgto.getTime() - dVenc.getTime()
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
                    sumDias += diffDays
                    if (diffDays < maxAdiantado) maxAdiantado = diffDays
                    if (diffDays > maxAtrasado) maxAtrasado = diffDays
                }
            }
            mediaDias = sumDias / pagas.length
        }

        return {
            pagas: pagas.length, pendentes: pendentes.length, atrasadas: atrasadas.length,
            totalValorRows: rows.reduce((a, r) => a + r.valor, 0),
            totalRowsCount: rows.length,
            totalPago: pagas.reduce((a, r) => a + r.valor, 0),
            totalPendente: pendentes.reduce((a, r) => a + r.valor, 0),
            totalAtrasado: atrasadas.reduce((a, r) => a + r.valor, 0),
            pctPagas: pct(pagas.length), pctPendentes: pct(pendentes.length),
            pctAtrasadas: pct(atrasadas.length), pctTotal: pct(rows.length),
            mediaDiasPagamento: mediaDias,
            maxAdiantado, maxAtrasado
        }
    }, [rows, filtradas])

    function formatBRL(v: number) {
        return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    }

    // Auxiliar para calcular a diferença de dias
    function calcDiasDiff(dPgtoStr: string, dVencStr: string): number {
        const dPgto = new Date(dPgtoStr + 'T12:00:00')
        const dVenc = new Date(dVencStr + 'T12:00:00')
        const diffTime = dPgto.getTime() - dVenc.getTime()
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    }

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-surface-900">Cobranças & Faturas</h2>
                    <p className="text-sm text-surface-700/60 mt-0.5">
                        De <strong>{dataInicio}</strong> até <strong>{dataFim}</strong> — ACSM + Multiveicular
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2 mb-2 sm:mb-0">
                        {syncState.running ? (
                            <div className="flex items-center gap-2 text-sm text-surface-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 h-8">
                                <Loader2 size={14} className="animate-spin text-amber-600" />
                                <span className="font-medium">{syncState.done}/{syncState.total}</span>
                            </div>
                        ) : syncState.done > 0 ? (
                            <div className="flex items-center gap-2 text-sm text-success-700 bg-success-50 border border-success-200 rounded-lg px-2 py-1.5 h-8 cursor-pointer hover:bg-success-100 transition-colors" title="Sincronizar Novamente" onClick={handleSyncAsaas}>
                                <Check size={14} />
                                <span className="font-medium whitespace-nowrap hidden sm:inline">{syncState.done} sincronizados</span>
                            </div>
                        ) : (
                            <button onClick={handleSyncAsaas} className="btn border border-primary-200 bg-primary-50 hover:bg-primary-100 text-primary-700 btn-sm gap-2 transition-colors">
                                <RefreshCw size={14} /> Sincronizar Asaas
                            </button>
                        )}
                        <button onClick={() => setModalNova(true)} className="btn btn-primary btn-sm gap-2">
                            <Plus size={14} /> Nova Cobrança
                        </button>
                    </div>
                    <div className="hidden sm:block w-px h-6 bg-surface-200 mx-1"></div>
                    <div className="flex items-center gap-1.5 border border-surface-200 rounded-lg bg-white px-2 py-1">
                        <label className="text-xs text-surface-700/60 font-medium">De</label>
                        <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} className="input px-1 py-0.5 min-h-0 border-transparent w-auto text-sm" />
                        <label className="text-xs text-surface-700/60 font-medium ml-2">Até</label>
                        <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} className="input px-1 py-0.5 min-h-0 border-transparent w-auto text-sm" />
                    </div>
                    <button onClick={carregar} className="btn btn-secondary btn-sm gap-2">
                        <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                    </button>
                    <button onClick={exportCSV} title="Exportar para Excel" className="btn btn-secondary btn-sm gap-2">
                        <Download size={14} />
                    </button>
                </div>
            </div>

            {/* Counters */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                {[
                    { label: 'Pagas', value: totais.pagas, sub: formatBRL(totais.totalPago), pct: totais.pctPagas, color: 'bg-success-50 border-success-200 text-success-700', key: 'paga' },
                    { label: 'Pendentes', value: totais.pendentes, sub: formatBRL(totais.totalPendente), pct: totais.pctPendentes, color: 'bg-warning-50 border-warning-200 text-warning-700', key: 'pendente' },
                    { label: 'Atrasadas', value: totais.atrasadas, sub: formatBRL(totais.totalAtrasado), pct: totais.pctAtrasadas, color: 'bg-danger-50 border-danger-200 text-danger-700', key: 'atrasada' },
                    { label: 'Total Geral', value: totais.totalRowsCount, sub: formatBRL(totais.totalValorRows), pct: totais.pctTotal, color: 'bg-primary-50 border-primary-200 text-primary-700', key: 'todos' },
                    {
                        label: 'Tempo Médio',
                        value: (() => {
                            if (totais.mediaDiasPagamento === 0) return 'Em dia'
                            const v = Math.abs(Math.round(totais.mediaDiasPagamento))
                            return `${v} dia${v > 1 ? 's' : ''}`
                        })(),
                        sub: (
                            <div className="flex flex-col gap-0.5 mt-1">
                                <span className={totais.mediaDiasPagamento <= 0 ? (totais.mediaDiasPagamento < 0 ? 'text-indigo-600 font-bold' : '') : 'text-orange-600 font-bold'}>
                                    {totais.mediaDiasPagamento < 0 ? 'Pagos Adiantados' : totais.mediaDiasPagamento > 0 ? 'Pagos Atrasados' : 'No dia do Venc.'}
                                </span>
                                {(totais.maxAdiantado < 0 || totais.maxAtrasado > 0) && (
                                    <div className="flex items-center gap-1.5 mt-1 text-[10px] font-medium opacity-80 whitespace-nowrap">
                                        {totais.maxAdiantado < 0 && <span className="text-indigo-600 px-1 bg-indigo-100 rounded">↓ {Math.abs(totais.maxAdiantado)}d máx.</span>}
                                        {totais.maxAtrasado > 0 && <span className="text-orange-600 px-1 bg-orange-100 rounded">↑ {totais.maxAtrasado}d máx.</span>}
                                    </div>
                                )}
                            </div>
                        ),
                        pct: '',
                        color: totais.mediaDiasPagamento <= 0 ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-orange-50 border-orange-200 text-orange-700',
                        key: 'tempo_medio',
                        nonClickable: true
                    },
                ].map((c: any) => (
                    <button key={c.key} onClick={() => c.nonClickable ? null : setStatusFiltro(s => s === c.key ? 'todos' : c.key)}
                        className={`rounded-xl border p-4 text-left transition-all w-full col-span-2 md:col-span-1 flex flex-col justify-between ${c.color} ${!c.nonClickable && statusFiltro === c.key ? 'ring-2 ring-offset-1' : ''} ${!c.nonClickable ? 'hover:opacity-80' : 'cursor-default'}`}>
                        <div>
                            <p className="text-2xl font-bold">{c.value} <span className="text-sm font-medium opacity-60">{c.pct}</span></p>
                            <p className="text-sm font-medium opacity-80">{c.label}</p>
                        </div>
                        {typeof c.sub === 'string' ? <p className="text-xs font-semibold mt-1 opacity-70">{c.sub}</p> : c.sub}
                    </button>
                ))}
            </div>

            {/* Filters */}
            <div className="glass-card p-4 flex flex-wrap gap-3 items-center relative z-20">
                <div className="relative flex-1 min-w-[180px]">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-700/40" />
                    <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Buscar por placa ou associado..."
                        className="input pl-9 text-sm" />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Filter size={14} className="text-surface-700/40" />
                    <select value={statusFiltro} onChange={e => setStatusFiltro(e.target.value)} className="input w-auto text-sm">
                        <option value="todos">Todos ({rows.length})</option>
                        <option value="paga">Pagas</option>
                        <option value="pendente">Pendentes</option>
                        <option value="atrasada">Atrasadas</option>
                    </select>
                    <select value={tipoFiltro} onChange={e => setTipoFiltro(e.target.value)} className="input w-auto text-sm">
                        <option value="todos">Todos os tipos</option>
                        <option value="BOLETO">Boleto</option>
                        <option value="PIX">Pix</option>
                        <option value="CREDIT_CARD">Cartão</option>
                        <option value="UNDEFINED">N/D</option>
                    </select>

                    <div className="relative" ref={servicoMenuRef}>
                        <button
                            onClick={() => setIsServicoMenuOpen(!isServicoMenuOpen)}
                            className={`input w-auto text-sm flex items-center justify-between gap-3 h-10 ${Object.keys(servicoFiltro).length > 0 ? 'border-primary-500 bg-primary-50 text-primary-900 shadow-sm' : ''}`}
                        >
                            <span className="truncate max-w-[140px]">
                                {Object.keys(servicoFiltro).length === 0 ? 'Todos os serviços' : `${Object.keys(servicoFiltro).length} serviços filtrados`}
                            </span>
                            <ChevronDown size={14} className="opacity-50" />
                        </button>

                        {isServicoMenuOpen && (
                            <div className="absolute top-11 right-0 w-64 bg-white border border-surface-200 shadow-xl shadow-surface-900/5 rounded-xl p-2 z-50 animate-in fade-in zoom-in-95 duration-200">
                                <div className="flex items-center justify-between px-2 pb-2 mb-2 border-b border-surface-100">
                                    <span className="text-xs font-bold text-surface-500 uppercase tracking-wide">Filtrar Serviços</span>
                                    {Object.keys(servicoFiltro).length > 0 && (
                                        <button onClick={() => setServicoFiltro({})} className="text-xs text-primary-600 hover:text-primary-700 font-medium px-2 py-1 rounded-md hover:bg-primary-50 transition-colors">Limpar</button>
                                    )}
                                </div>
                                <div className="space-y-1 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
                                    {[{ key: '', label: 'Mensalidade (sem extra)' }, ...SERVICOS].map(s => {
                                        const state = servicoFiltro[s.key]
                                        return (
                                            <button
                                                key={s.key}
                                                onClick={() => toggleServicoFilter(s.key)}
                                                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors text-left font-medium ${state === 'include' ? 'bg-success-50 text-success-800' : state === 'exclude' ? 'bg-danger-50 text-danger-800' : 'hover:bg-surface-50 text-surface-700'}`}
                                            >
                                                <span>{s.label}</span>
                                                {state === 'include' && <Check size={14} className="text-success-600" />}
                                                {state === 'exclude' && <X size={14} className="text-danger-600" />}
                                            </button>
                                        )
                                    })}
                                </div>
                                <div className="px-2 pt-2 mt-2 border-t border-surface-100">
                                    <p className="text-[10px] text-surface-500 leading-tight">Clique 1x para garantir, 2x para ocultar.</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <button
                        onClick={() => setExibirExcluidas(v => !v)}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${exibirExcluidas ? 'bg-danger-50 border-danger-200 text-danger-700' : 'bg-surface-100 border-surface-200 text-surface-700 hover:bg-surface-200'}`}>
                        {exibirExcluidas ? <Eye size={14} /> : <EyeOff size={14} />}
                        Excluídas
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="glass-card overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 size={28} className="animate-spin text-primary-600" />
                    </div>
                ) : filtradas.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <Car size={40} className="text-surface-700/20 mb-3" />
                        <p className="text-surface-700/60">Nenhuma cobrança com placa encontrada</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th className="cursor-pointer hover:bg-surface-50 select-none whitespace-nowrap" onClick={() => requestSort('placa')}>
                                        Placa {getSortIcon('placa')}
                                    </th>
                                    <th className="cursor-pointer hover:bg-surface-50 select-none whitespace-nowrap" onClick={() => requestSort('associado_nome')}>
                                        Associado {getSortIcon('associado_nome')}
                                    </th>
                                    <th className="cursor-pointer hover:bg-surface-50 select-none whitespace-nowrap" onClick={() => requestSort('billingType')}>
                                        Tipo {getSortIcon('billingType')}
                                    </th>
                                    <th className="cursor-pointer hover:bg-surface-50 select-none whitespace-nowrap" onClick={() => requestSort('valor')}>
                                        Valor {getSortIcon('valor')}
                                    </th>
                                    <th className="cursor-pointer hover:bg-surface-50 select-none whitespace-nowrap" onClick={() => requestSort('data_vencimento')}>
                                        Vencimento {getSortIcon('data_vencimento')}
                                    </th>
                                    <th className="cursor-pointer hover:bg-surface-50 select-none whitespace-nowrap" onClick={() => requestSort('tempo')}>
                                        Tempo Pgto {getSortIcon('tempo')}
                                    </th>
                                    <th className="cursor-pointer hover:bg-surface-50 select-none whitespace-nowrap" onClick={() => requestSort('status')}>
                                        Status {getSortIcon('status')}
                                    </th>
                                    <th className="hidden lg:table-cell">Descrição</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedFiltradas.map(r => (
                                    <tr key={r.id}
                                        className={`transition-colors cursor-pointer ${r.deletedAt ? 'opacity-50 bg-danger-50/30' : 'hover:bg-surface-50'}`}
                                        onClick={() => setSelectedFatura(r)}>
                                        <td>
                                            <span className="font-mono font-bold text-sm bg-surface-100 px-2 py-1 rounded-lg">
                                                {r.placa}
                                            </span>
                                        </td>
                                        <td className="text-sm font-medium">{r.associado_nome}</td>
                                        <td>
                                            <span className="text-xs font-semibold bg-surface-100 text-surface-700 px-2 py-0.5 rounded-full">
                                                {traduzBilling(r.billingType)}
                                            </span>
                                        </td>
                                        <td className="font-bold text-sm">R$ {r.valor.toFixed(2)}</td>
                                        <td className="text-sm text-surface-700">{formatDate(r.data_vencimento)}</td>
                                        <td className="text-sm text-surface-700">
                                            {r.data_pagamento ? (
                                                <div className="flex flex-col">
                                                    <span>{formatDate(r.data_pagamento)}</span>
                                                    {(() => {
                                                        const diff = calcDiasDiff(r.data_pagamento, r.data_vencimento)
                                                        if (diff === 0) return <span className="text-[10px] text-surface-500 font-medium tracking-tight">Em dia</span>
                                                        if (diff < 0) return <span className="text-[10px] text-indigo-600 font-medium tracking-tight">{Math.abs(diff)}d adiantado</span>
                                                        return <span className="text-[10px] text-orange-600 font-medium tracking-tight mt-0.5"><span className="bg-orange-100 px-1 py-[1px] rounded">{diff}d atrasado</span></span>
                                                    })()}
                                                </div>
                                            ) : <span className="text-surface-400">—</span>}
                                        </td>
                                        <td>
                                            <span className={`badge ${getStatusBadgeClass(r.status)}`}>
                                                {translateStatus(r.status)}
                                            </span>
                                        </td>
                                        <td className="hidden lg:table-cell text-sm text-surface-700/70 max-w-xs truncate">
                                            {r.deletedAt && <EyeOff size={12} className="inline mr-1 text-danger-500" />}{r.descricao || '—'}
                                        </td>
                                        <td onClick={e => e.stopPropagation()}>
                                            <div className="flex items-center justify-end gap-1">
                                                {!r.deletedAt && r.status !== 'paga' && r.status !== 'cancelada' && (
                                                    <button onClick={() => setModalDinheiro(r)} title="Marcar como Pago em Dinheiro" className="p-1.5 text-success-600 hover:bg-success-50 rounded-lg transition-colors"><DollarSign size={16} /></button>
                                                )}
                                                {!r.deletedAt && r.status !== 'cancelada' && (
                                                    <>
                                                        <button onClick={() => setModalTipo(r)} title="Alterar Tipo de Cobrança" className="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"><CreditCard size={16} /></button>
                                                        <button onClick={() => setModalValor(r)} title="Alterar Valor" className="p-1.5 text-warning-600 hover:bg-warning-50 rounded-lg transition-colors"><Edit3 size={16} /></button>
                                                        <button onClick={() => setModalExclusao(r)} title="Excluir/Cancelar no Asaas" className="p-1.5 text-danger-600 hover:bg-danger-50 rounded-lg transition-colors"><Trash2 size={16} /></button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="px-4 py-3 border-t border-surface-100 flex items-center justify-between">
                            <span className="text-sm text-surface-700/60">{sortedFiltradas.length} cobranças exibidas</span>
                            <span className="text-sm font-bold text-surface-900">Total: {formatBRL(totais.totalValorRows)}</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Drawer */}
            <FaturaDetailDrawer
                fatura={selectedFatura}
                onClose={() => setSelectedFatura(null)}
                onMarcarPaga={id => marcarPagaMutation.mutate(id)}
                onGerarProRata={id => proRataMutation.mutate(id)}
            />

            {/* Modais */}
            {
                modalTipo && (
                    <ModalAlterarTipo
                        row={modalTipo}
                        taxasDb={taxasDb}
                        onClose={() => setModalTipo(null)}
                        onSave={tipo => alterarTipo(modalTipo, tipo)}
                    />
                )
            }
            {
                modalValor && (
                    <ModalAlterarValor
                        row={modalValor}
                        onClose={() => setModalValor(null)}
                        onSave={valor => alterarValor(modalValor, valor)}
                    />
                )
            }
            {
                modalNova && (
                    <ModalNovaCobranca
                        templatesDb={templatesDb}
                        onClose={() => setModalNova(false)}
                        onSave={criarNovaCobranca}
                    />
                )
            }
            {
                modalExclusao && (
                    <ModalConfirmarExclusao
                        row={modalExclusao}
                        onClose={() => setModalExclusao(null)}
                        onConfirm={() => excluirConfirmado(modalExclusao)}
                    />
                )
            }
            {
                modalDinheiro && (
                    <ModalReceberEmDinheiro
                        row={modalDinheiro}
                        onClose={() => setModalDinheiro(null)}
                        onConfirm={(d) => pagoEmDinheiro(modalDinheiro, d)}
                    />
                )
            }
        </div >
    )
}
